<template>
  <section class="position-relative" :class="sectionClass" :style="sectionStyles">
    <!-- background/hero image -->
    <div class="hda-header">
      <slot name="headerBg">
        <img class="hda-header-image" :src="bgImageSource" :alt="heroAltText" loading="eager" />
      </slot>
    </div>
    <!-- content -->
    <div ref="hdaContainer" class="container-lg pb-space-md pb-md-space-lg position-relative">
      <div class="mx-space-xxs degree-intro-top position-relative">
        <div v-if="slots.aboveContent" ref="aboveContent" class="above-content-slot">
          <slot name="aboveContent"> </slot>
        </div>
        <div class="row no-gutters" :class="containerClass">
          <div class="col-12 col-md-6 col-lg-8">
            <div class="mx-space-sm mx-lg-space-md mb-space-sm mb-md-space-md">
              <h1 class="mb-space-md mb-md-space-sm pt-space-sm pt-md-space-md" :class="titleClass">
                {{ title }}
              </h1>
              <slot name="text" />
              <a
                :target="linkTarget"
                :class="linkClass"
                :href="linkUrl"
                :aria-label="linkText"
                @click="handleClick"
              >
                {{ linkText }}
              </a>
            </div>
          </div>
          <div class="col-12 col-md-6 col-lg-4">
            <div class="mx-space-sm ms-md-space-md ms-lg-space-sm mb-space-md mt-md-space-md">
              <div class="fact-card border-light" :class="factCardClass">
                <div class="pt-space-lg px-space-sm px-md-space-md pb-space-lg">
                  <p
                    class="mb-space-sm px-space-xxs degree-label d-inline-block"
                    :class="factCardTitleClass"
                  >
                    {{ infoTitle }}
                  </p>

                  <div class="d-flex mb-space-xs">
                    <img
                      :src="CalendarAlt"
                      alt="Next start date: "
                      class="me-space-xxs fs-xs icon-class"
                      width="16"
                      height="16"
                    />
                    <p class="mb-0" :class="factCardItemClass">
                      Next start date:
                      <span class="fw-bold">{{ nextStartDate }}</span>
                    </p>
                  </div>

                  <hr class="text-dark-3 mb-space-xs mt-0 fact-block-break" />

                  <div
                    v-for="(item, itemIndex) in infoList"
                    :key="itemIndex"
                    class="d-flex mb-space-xxs"
                  >
                    <img
                      :src="CheckCircle"
                      :alt="item.description as string"
                      class="me-space-xxs fs-xs icon-class"
                      width="16"
                      height="16"
                    />
                    <p class="mb-0 quick-fact-copy" :class="factCardItemClass">
                      <span>
                        {{ item.description }}
                        <span class="fw-bold">{{ item.value }}</span>
                      </span>
                      <ToolTip
                        v-if="item.tooltip"
                        class="quick-fact-tooltip"
                        placement="bottom-start"
                        :offset="8"
                        :tooltip-width="260"
                        :display-shadow="false"
                        :tooltip-id="`quick-fact-tooltip-${itemIndex}`"
                        aria-label="More information"
                      >
                        <template #tooltip>
                          <div class="quick-fact-tooltip-content fs-medium">
                            {{ item.tooltip }}
                          </div>
                        </template>
                        <img
                          :src="CircleInfo"
                          alt=""
                          class="fact-tooltip-icon"
                          width="16"
                          height="16"
                        />
                      </ToolTip>
                    </p>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
        <slot name="belowContent"></slot>
      </div>
    </div>
  </section>
</template>

<script setup lang="ts">
import CircleInfo from "@/assets/icons/Icon-circle-info-solid.svg";
import CalendarAlt from "@/assets/img/calendar-alt.svg";
import CheckCircle from "@/assets/img/check-circle.svg";
import ToolTip from "@/components/ToolTip.vue";
import type { LinkTarget, TitleVariant, VariantOption } from "@/types/types";
import { useWindowSize } from "@vueuse/core";

interface InfoListType {
  value: string | number;
  description: string;
  tooltip?: string | null;
}
const props = withDefaults(
  defineProps<{
    infoList: InfoListType[];
    nextStartDate: string;
    linkTarget?: LinkTarget;
    linkUrl?: string;
    linkTitle?: string;
    linkText?: string;
    linkAsButton?: boolean;
    linkButtonVariant?: VariantOption;
    linkTextSize?: TitleVariant;
    linkTextVariant?: VariantOption;
    linkTextWeight?: string;
    containerBgColor?: VariantOption;
    sectionBgColor?: VariantOption;
    factCardBgColor?: VariantOption;
    factCardItemTextColor?: VariantOption;
    factCardTitleSize?: TitleVariant;
    factCardTitleColor?: VariantOption;
    factCardTitleBgColor?: VariantOption;
    bgImageSource: string;
    altText: string;
    infoTitle?: string;
    title: string;
    titleSize?: TitleVariant;
    titleVariant?: VariantOption;
  }>(),
  {
    linkTarget: "_self",
    linkUrl: "https://asuonline.asu.edu/",
    linkTitle: "Lorem ipsum",
    linkText: "Lorem ipsum",
    linkAsButton: false,
    linkButtonVariant: "primary",
    linkTextSize: "medium",
    linkTextVariant: "primary",
    linkTextWeight: "normal",
    containerBgColor: "white",
    sectionBgColor: "light-2",
    factCardBgColor: "light-2",
    factCardItemTextColor: "dark-3",
    factCardTitleSize: "small",
    factCardTitleColor: "white",
    factCardTitleBgColor: "dark-3",
    infoTitle: "Quick facts",
    titleSize: "medium",
    titleVariant: "dark-3"
  }
);

const minHeroHeight = ref();
const slots = useSlots();
const aboveContent = ref();
const emit = defineEmits(["onLinkClick"]);
const { width } = useWindowSize();

const factCardClass = computed(() => {
  return `bg-${props.factCardBgColor}`;
});
const factCardItemClass = computed(() => {
  return `text-${props.factCardItemTextColor}`;
});
const heroAltText = computed(() => {
  return props.altText?.trim() ?? "";
});
const containerClass = computed(() => {
  return `bg-${props.containerBgColor}`;
});
const titleClass = computed(() => {
  return `h1-${props.titleSize} text-${props.titleVariant}`;
});
const sectionClass = computed(() => {
  return `bg-${props.sectionBgColor}`;
});
const sectionStyles = computed(() => {
  // sets min height to make sure the content above the container always fits in the hero image section
  return slots.aboveContent ? `--header-image-min-height: ${minHeroHeight.value}px;` : null;
});
const factCardTitleClass = computed(() => {
  return `fs-${props.factCardTitleSize} text-${props.factCardTitleColor} bg-${props.factCardTitleBgColor}`;
});
const linkClass = computed(() => {
  let classNames = "";

  classNames += props.linkAsButton
    ? `px-space-xs py-space-xxs btn btn-${props.linkButtonVariant} `
    : "text-decoration-underline ";
  classNames += `fs-${props.linkTextSize} text-${props.linkTextVariant} fw-${props.linkTextWeight}`;

  return classNames;
});

watch(width, () => {
  // sets min height of hero image to make sure content in aboveContent slot never exceeds window height.
  if (slots.aboveContent && aboveContent.value.scrollHeight) {
    minHeroHeight.value = aboveContent.value.scrollHeight + 96;
  }
});

const handleClick = (event: Event) => {
  emit("onLinkClick", event);
};
</script>

<style scoped lang="scss">
.fact-block-break {
  height: 0.25px;
}

.hda-header {
  height: 256px;
}

.hda-header-image {
  display: block;
  width: 100%;
  height: 256px;
  min-height: var(--header-image-min-height, 0);
  object-fit: cover;
  object-position: center top;
}

.fact-card {
  background-color: var(--light-2, #f1f1f1);
  border-radius: 3px;
  border-left: 4px solid var(--secondary, #ffc627) !important;
  min-height: 295px;
  max-width: 320px;
}

.degree-label {
  font-weight: 700;
  padding: 4px;
  color: var(--white, #ffffff);
}

.icon-class {
  width: 16px;
  height: 16px;
  margin-top: 0.25rem;
}

.fact-tooltip-icon {
  width: 16px;
  height: 16px;
  display: block;
  filter: brightness(0) saturate(100%) invert(18%) sepia(50%) saturate(1795%) hue-rotate(312deg)
    brightness(87%) contrast(98%);
}

.quick-fact-copy {
  display: flex;
  align-items: center;
  flex-wrap: wrap;
}

.quick-fact-tooltip {
  display: inline-flex;
  flex: 0 0 18px;
  align-items: center;
  margin-left: 0.25rem;
}

.quick-fact-tooltip :deep(.tool-tip-icon) {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  width: 18px;
  height: 18px;
  line-height: 1;
  margin-left: 0 !important;
}

.quick-fact-tooltip :deep(.tool-tip-floating) {
  overflow: visible;
  width: min(16.25rem, calc(100vw - 2rem)) !important;
  max-width: min(16.25rem, calc(100vw - 2rem)) !important;
  padding: 0 !important;
  border-radius: 0 !important;
  background: transparent !important;
  color: var(--white, #ffffff);
  z-index: 1200;
}

.quick-fact-tooltip-content {
  position: relative;
  box-sizing: border-box;
  width: min(16.25rem, calc(100vw - 2rem));
  max-width: min(16.25rem, calc(100vw - 2rem));
  padding: 1rem;
  background: var(--white, #ffffff);
  color: rgb(25, 25, 25);
  line-height: 1.45;
  box-shadow: 0 0.5rem 1rem rgba(0, 0, 0, 0.15);
}

.degree-intro-top {
  margin-top: -48px;
}

.above-content-slot {
  position: absolute;
  bottom: 100%;
}

/* Large devices (desktops, 992px and up) */
@media (min-width: 992px) {
  .hda-header,
  .hda-header-image {
    height: 512px;
  }
  .degree-intro-top {
    margin-top: -96px;
  }
}

@media (min-width: 1200px) {
  .hda-header,
  .hda-header-image {
    height: 512px;
  }
  .hda-header-image {
    background-attachment: scroll;
  }
}
</style>
